import React, { useState, useMemo } from 'react';
import { Search, ShoppingBag, Star, Droplet, Sparkles, X, Check, Cloud, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';

// --- CONFIGURATION ---
const CONTACT_WHATSAPP = "+13657395999"; 
const CONTACT_INSTAGRAM = "scentcloud1";
const PRICES = { '3ml': 5, '5ml': 8, '10ml': 15 };

// --- CUSTOM ICONS ---
const InstagramIcon = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line>
  </svg>
);

// --- DATA ---
const products = [
  { 
    id: 1, name: "Hawas Kobra", gender: "More Masculine", notes: "Green Tea, Amber", rating: 4.2, 
    cloneOf: "Louis Vuitton's Imagination", cloneOfPrice: 283,
    description: "A sweet, aquatic powerhouse that brings major \"invincible\" energy. Think fresh marine notes mixed with juicy plum and warm amber for a legendary, everyday crowd-pleaser.",
    images: ["/images/Hawas Kobra LV Imagination.jpg", "/images/Hawas Kobra Rasasi.jpg", "/images/Imagination Louis Vuitton.jpg"]
  },
  { 
    id: 2, name: "Supremacy Collector's Edition", gender: "Loved by all genders", notes: "Pineapple, Oakmoss", rating: 4.8, 
    cloneOf: "Creed Absolu Aventus", cloneOfPrice: 904,
    description: "A kingly blend of vibrant pineapple, rich birch, and a hint of sophisticated smoke. This is the \"emperor\" of fragrances, perfect for making a highly confident, masculine statement.",
    images: ["/images/Supremacy Collectors Creed Aventus.jpg", "/images/Supremacy Collectors Edition Pour Homme.jpeg", "/images/Absolu Aventus Creed.jpg"]
  },
  { 
    id: 3, name: "Rayhaan Terra EDP", gender: "More Masculine", notes: "Saffron, Incense", rating: 4.0, 
    cloneOf: "Amouage Outlands", cloneOfPrice: 735,
    description: "An earthy, grounded masterpiece featuring a distinct, bitter orange opening that settles into sharp flint and deep vetiver. A true \"earthy\" classic for the refined gentleman.",
    images: ["/images/Rayhaan Terra Outlands Amouage.jpg", "/images/Rayhaan Terra.jpg", "/images/Outlands Amouage.jpg"]
  },
  { 
    id: 4, name: "Arabiyat Prestige Ramad Oriental EDP", gender: "More Masculine", notes: "Amber, Vanilla", rating: 4.2, 
    cloneOf: "Amouage Outlands", cloneOfPrice: 650,
    description: "A deeply rich, smoky, and resinous amber fragrance. It captures the luxurious essence of dark woods and spices, perfect for those who love majestic, mysterious oriental profiles.",
    images: ["/images/Ramad Oriental Outlands Amouage.jpg", "/images/Ramad Oriental Arabiyat Prestige.jpg", "/images/Outlands Amouage.jpg"]
  },
  { 
    id: 5, name: "Liquid Brun EDP", gender: "Loved by all genders", notes: "Bourbon Vanilla, Cinnamon", rating: 4.5, 
    cloneOf: "Parfums de Marly Althair", cloneOfPrice: 339,
    description: "A warm, intoxicating embrace of bourbon vanilla, sweet praline, and bright orange blossom. A deeply luxurious, \"noble\" gourmand that feels incredibly rich and comforting.",
    images: ["/images/Liquid Brun French Avenue.jpg", "/images/Althaïr Parfums de Marly.jpg", "/images/Liquid Brun Althair.jpg"]
  },
  { 
    id: 6, name: "Camaro Homme Intense EDP", gender: "More Masculine", notes: "Iris, Cocoa", rating: 4.4, 
    cloneOf: "Dior Homme Intense", cloneOfPrice: 198,
    description: "A sophisticated evening scent dominated by a luxurious, powdery iris note layered with warm cocoa and woods. The absolute pinnacle of elegant, \"intense\" modern menswear.",
    images: ["/images/Camaro Homme Intense Pendora Scents.jpg", "/images/Dior Homme Intense.jpg"]
  },
  { 
    id: 7, name: "MontBlanc Individuel", gender: "More Masculine", notes: "Lavender, Raspberry", rating: 3.8, 
    cloneOf: "Creed Original Santal", cloneOfPrice: 565,
    description: "A brilliantly fresh and sweet blend of raspberry, cinnamon, and smooth sandalwood. If you love an \"original\" take on a sweet, clean, almost freshly-laundered profile, this is your signature scent.",
    images: ["/images/Montblanc Individuel.jpg", "/images/Creed Original Santal.jpg"]
  },
  { 
    id: 8, name: "Missoni Parfum Pour Homme EDP", gender: "More Masculine", notes: "Lavender, Patchouli", rating: 4.1, 
    cloneOf: "Bleu de Chanel", cloneOfPrice: 240,
    description: "The ultimate versatile \"blue\" fragrance. It opens with bright grapefruit and lemon before settling into smooth, sophisticated woods—the perfect, everyday \"bleu\" signature.",
    images: ["/images/Missoni Bleu de Channel.jpg", "/images/Missoni Parfum Pour Homme.jpg", "/images/Bleu de Chanel.jpg"]
  },
  { 
    id: 9, name: "Gulf Orchid Mango Ice EDP", gender: "Loved by all genders", notes: "Mango, Vanilla", rating: 4.0, 
    cloneOf: "God of Fire by Stephane Humbert Lucas", cloneOfPrice: 367,
    description: "A vibrant, hyper-realistic tropical blast of juicy mango with a surprisingly cool, frosty finish. A \"god-tier\" fruity fragrance that feels exactly like a frozen, high-end cocktail on a beach.",
    images: ["/images/Mango Ice God of Fire.jpg", "/images/Mango Ice Gulf Orchid.jpg", "/images/God of Fire Stéphane Humbert Lucas 777.jpg"]
  },
  { 
    id: 10, name: "Afnan 9Pm (Ultramale Twist) EDP", gender: "More Masculine", notes: "Vanilla, Cinnamon", rating: 4.3, 
    cloneOf: "Jean Paul Gaultier’s Ultra Male", cloneOfPrice: 170,
    description: "The quintessential loud, sweet, and playful party scent. Packed with juicy pear, dark vanilla, and vibrant spices, this is the \"ultra\" choice for a night out on the town.",
    images: ["/images/Afnan 9pm.jpg", "/images/Ultra Male Jean Paul Gaultier.jpg"]
  },
  { 
    id: 11, name: "Bentley Black Edition EDP", gender: "More Masculine", notes: "Tobacco, Patchouli", rating: 3.9, 
    cloneOf: "", cloneOfPrice: null,
    description: "A sleek, sharp, and modern woody-floral scent with distinct violet leaf and earthy notes. It wears beautifully, much like a perfectly tailored, dark suit for the modern professional.",
    images: ["/images/Bentley For Men Black Edition.jpg"]
  },
  { 
    id: 12, name: "Moschino Uomo Moschino EDT", gender: "More Masculine", notes: "Kumquat, Cedar", rating: 4.0, 
    cloneOf: "Burberry Touch", cloneOfPrice: 124,
    description: "A brilliantly classic, Italian barbershop-style fragrance featuring a unique kumquat note, wrapped in smooth musk and sun-drenched florals. A timeless, masculine standard.",
    images: ["/images/Uomo Moschino.jpg", "/images/Burberry Touch.jpg"]
  }
];

// --- HELPERS ---
const getGenderStyles = (gender) => {
  switch (gender) {
    case "More Masculine": return "bg-blue-100 text-blue-700";
    case "More Feminine": return "bg-pink-100 text-pink-700";
    case "Loved by all genders": return "bg-purple-100 text-purple-700";
    default: return "bg-slate-100 text-slate-600";
  }
};

// --- COMPONENTS ---

const SuccessModal = ({ isOpen, onClose, onGoToCart }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center">
        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check size={32} />
        </div>
        <h3 className="text-xl font-bold mb-2">Added to Cart!</h3>
        <p className="text-slate-500 mb-6">Your fragrance is ready for checkout.</p>
        <div className="flex flex-col gap-3">
          <button onClick={onClose} className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors">
            I want more
          </button>
          <button onClick={onGoToCart} className="w-full py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-colors">
            Go to cart >>>
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductCard = ({ product, onAddToCart, onExpandImage }) => {
  const [selectedSize, setSelectedSize] = useState('5ml');
  const [currentImg, setCurrentImg] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const nextImg = (e) => {
    e.stopPropagation();
    setCurrentImg((prev) => (prev + 1) % product.images.length);
  };

  const prevImg = (e) => {
    e.stopPropagation();
    setCurrentImg((prev) => (prev - 1 + product.images.length) % product.images.length);
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 flex flex-col h-full">
      <div className="relative w-full h-56 mb-4 rounded-xl overflow-hidden bg-slate-50 group">
        <img 
          src={product.images[currentImg]} 
          alt={product.name} 
          className="w-full h-full object-cover cursor-zoom-in transition-transform duration-500 hover:scale-105" 
          onClick={() => onExpandImage(product.images[currentImg])}
        />
        
        {product.images.length > 1 && (
          <>
            <button onClick={prevImg} className="absolute left-2 top-1/2 -translate-y-1/2 p-1 bg-white/80 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronLeft size={20} />
            </button>
            <button onClick={nextImg} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 bg-white/80 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronRight size={20} />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {product.images.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all ${i === currentImg ? 'w-4 bg-indigo-600' : 'w-1.5 bg-slate-300'}`} />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="flex justify-between items-start mb-2">
        <h3 className="font-bold text-lg text-slate-800 leading-tight">{product.name}</h3>
        <span className={`${getGenderStyles(product.gender)} text-[10px] font-bold px-2 py-1 uppercase rounded-md ml-2 whitespace-nowrap`}>
          {product.gender}
        </span>
      </div>

      <div className="mb-3">
        <p className={`text-sm text-slate-500 italic ${!isExpanded ? 'line-clamp-2' : ''}`}>
          "{product.description}"
        </p>
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-indigo-600 text-xs font-bold mt-1 hover:underline"
        >
          {isExpanded ? 'less' : 'more'}
        </button>
      </div>

      {product.cloneOf && (
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5 mb-4">
          <p className="text-xs text-amber-800 font-medium flex items-center gap-1.5">
            <Sparkles size={14} className="text-amber-500 shrink-0" />
            <span>Inspired by <span className="font-bold">{product.cloneOf}</span></span>
          </p>
        </div>
      )}

      <div className="flex-grow space-y-3">
        <p className="text-sm text-slate-600 flex items-start gap-2">
          <Droplet size={16} className="text-indigo-400 mt-0.5 shrink-0" />
          <span><span className="font-semibold text-slate-700">Notes:</span> {product.notes}</span>
        </p>
      </div>

      <div className="mt-5 pt-4 border-t border-slate-100">
        <div className="flex gap-2 mb-4 bg-slate-50 p-1 rounded-lg">
          {Object.keys(PRICES).map(size => (
            <button key={size} onClick={() => setSelectedSize(size)} className={`flex-1 py-1.5 rounded-md text-sm font-bold transition-all ${selectedSize === size ? 'bg-white text-indigo-600 shadow-sm border border-slate-200' : 'text-slate-500'}`}>
              {size}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xl font-bold text-slate-800">${PRICES[selectedSize]}</span>
          <button 
            onClick={() => onAddToCart(product, selectedSize, PRICES[selectedSize])} 
            className="px-5 py-2.5 rounded-xl font-bold text-sm bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [genderFilter, setGenderFilter] = useState('All');
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const s = searchTerm.toLowerCase();
      const matchesSearch = p.name.toLowerCase().includes(s) || p.notes.toLowerCase().includes(s);
      const matchesGender = genderFilter === 'All' || p.gender === genderFilter;
      return matchesSearch && matchesGender;
    });
  }, [searchTerm, genderFilter]);

  const addToCart = (product, size, price) => {
    setCart(prev => {
      const idx = prev.findIndex(item => item.product.id === product.id && item.size === size);
      if (idx > -1) {
        const newCart = [...prev];
        newCart[idx].quantity += 1;
        return newCart;
      }
      return [...prev, { product, size, price, quantity: 1 }];
    });
    setShowSuccess(true);
  };

  const removeFromCart = (productId, size) => {
    setCart(prev => prev.filter(item => !(item.product.id === productId && item.size === size)));
  };

  const cartTotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${CONTACT_WHATSAPP}`, '_blank');
  };

  const handleInstagram = () => {
    window.open(`https://instagram.com/${CONTACT_INSTAGRAM}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800">
      <SuccessModal 
        isOpen={showSuccess} 
        onClose={() => setShowSuccess(false)} 
        onGoToCart={() => { setShowSuccess(false); setIsCartOpen(true); }} 
      />

      <nav className="bg-white sticky top-0 z-40 border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Cloud size={20} className="text-indigo-600" />
            <h1 className="text-xl font-extrabold">myscent<span className="text-indigo-600">.cloud</span></h1>
          </div>
          
          <div className="flex items-center gap-3">
            <button onClick={handleWhatsApp} className="p-2 text-green-600 hover:bg-green-50 rounded-full transition-colors">
              <MessageCircle size={22} />
            </button>
            <button onClick={handleInstagram} className="p-2 text-pink-600 hover:bg-pink-50 rounded-full transition-colors">
              <InstagramIcon size={22} />
            </button>
            <button onClick={() => setIsCartOpen(true)} className="relative p-2 text-slate-600">
              <ShoppingBag size={24} />
              {cart.length > 0 && (
                <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full border-2 border-white">
                  {cart.reduce((a, b) => a + b.quantity, 0)}
                </span>
              )}
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">
            myscent<span className="text-indigo-600">.cloud</span>
          </h2>
          <p className="text-lg md:text-xl font-medium">
            Curated Scents. <span className="text-indigo-600">Because your vibe speaks before you do.</span>
          </p>
          <p className="text-[11px] text-slate-400 italic mt-6 max-w-lg mx-auto leading-relaxed">
            All samples are hand-decanted from 100% authentic original fragrances. <br /> 
            We are an independent entity and are not affiliated with, or endorsed by, the trademarked designer brands.
          </p>

          <div className="flex justify-center gap-3 mt-8">
            <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-200 text-sm font-bold">3ml = $5</div>
            <div className="bg-slate-200 px-4 py-2 rounded-xl shadow-sm border border-slate-300 text-sm font-bold">5ml = $8</div>
            <div className="bg-amber-100 px-4 py-2 rounded-xl shadow-sm border border-amber-200 text-sm font-bold text-amber-800">10ml = $15</div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input 
              type="text" 
              placeholder="Search by name or notes..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20" 
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
            {['All', 'More Masculine', 'More Feminine', 'Loved by all genders'].map(g => (
              <button 
                key={g} 
                onClick={() => setGenderFilter(g)} 
                className={`px-4 py-3 rounded-xl font-bold text-xs whitespace-nowrap transition-colors ${genderFilter === g ? 'bg-slate-800 text-white' : 'bg-white border border-slate-200 hover:bg-slate-50'}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map(p => <ProductCard key={p.id} product={p} onAddToCart={addToCart} onExpandImage={setExpandedImage} />)}
        </div>
      </main>

      {isCartOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex justify-end">
          <div className="bg-white w-full max-w-md h-full flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-xl font-bold flex items-center gap-2"><ShoppingBag size={24} /> Your Cart</h2>
              <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-slate-100 rounded-full"><X size={20} /></button>
            </div>
            <div className="flex-grow overflow-y-auto p-5 space-y-4">
              {cart.length === 0 ? (
                <div className="text-center py-20">
                  <ShoppingBag size={48} className="mx-auto text-slate-200 mb-4" />
                  <p className="text-slate-500">Your cart is empty.</p>
                </div>
              ) : cart.map((item, i) => (
                <div key={i} className="flex gap-4 p-4 border rounded-2xl bg-white shadow-sm">
                  <img src={item.product.images[0]} alt={item.product.name} className="w-16 h-16 rounded-xl object-cover" />
                  <div className="flex-grow">
                    <h4 className="font-bold text-slate-800">{item.product.name}</h4>
                    <p className="text-sm text-slate-500">{item.size} - Qty: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-lg">${item.price * item.quantity}</span>
                    <button onClick={() => removeFromCart(item.product.id, item.size)} className="block text-xs text-red-500 font-bold mt-2 hover:underline">Remove</button>
                  </div>
                </div>
              ))}
            </div>
            {cart.length > 0 && (
              <div className="p-5 border-t bg-slate-50 space-y-3">
                <div className="flex justify-between items-center mb-4"><span className="font-bold">Total</span><span className="font-extrabold text-2xl text-indigo-600">${cartTotal}</span></div>
                <button onClick={handleWhatsApp} className="w-full py-4 rounded-xl font-bold bg-[#25D366] text-white flex items-center justify-center gap-2 hover:opacity-90">
                  <MessageCircle size={20} /> Order via WhatsApp
                </button>
                <button onClick={handleInstagram} className="w-full py-4 rounded-xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white flex items-center justify-center gap-2 hover:opacity-90">
                  <InstagramIcon size={20} /> Order via Instagram
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {expandedImage && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setExpandedImage(null)}>
          <img src={expandedImage} alt="Expanded" className="max-w-full max-h-[90vh] rounded-xl shadow-2xl" />
        </div>
      )}
    </div>
  );
}